import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from 'vue-router'

import { useSessionStore } from '@/stores/sessionStore'
import ApplicationDetailsView from '@/views/ApplicationDetailsView.vue'
import ApplicationsView from '@/views/ApplicationsView.vue'
import InviteDisplay from '@/components/login/InviteDisplay.vue'
import LoginFormVue from '@/components/login/LoginForm.vue'
import DashboardView from '@/views/DashboardView.vue'
import DocumentsView from '@/views/DocumentsView.vue'
import GroupsView from '@/views/GroupsView.vue'
import PeopleView from '@/views/PeopleView.vue'
import AutomationsView from '@/views/AutomationsView.vue'
import AccountingImportDetailView from '@/views/AccountingImportDetailView.vue'
import AccountingImportDetailCustomHeader from '@/components/accounting/AccountingImportDetailCustomHeader.vue'
import TestView from '@/views/testView.vue'
import Test2View from '@/views/test2View.vue'
import LicensesView from '@/views/LicensesView.vue'
import DataSourcesView from '@/views/DataSourcesView.vue'
import DataSourceDetailsView from '@/views/DataSourceDetailsView.vue'
import DataSourceDetailCustomHeader from '@/components/discovery/DataSourceDetailCustomHeader.vue'
import CompanySelect from '@/components/company/CompanySelect.vue'
import ApplicationDetailsViewDocuments from '@/views//tabs/ApplicationDetailsViewDocuments.vue'
import ApplicationDetailsViewNotes from '@/views//tabs/ApplicationDetailsViewNotes.vue'
import ApplicationDetailsViewOptimizations from '@/views//tabs/ApplicationDetailsViewOptimizations.vue'
import ApplicationDetailsViewLicenses from '@/views//tabs/ApplicationDetailsViewLicenses.vue'
import ApplicationDetailsViewAccounts from '@/views/tabs/ApplicationDetailsViewAccounts.vue'
import { ApplicationStore } from '@/stores/application/application.store'
import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
import {
  normalizeSubAccountParam,
  selectPreferredSubAccount,
} from '@/utils/useSubAccountResolver'

import NotFoundView from '@/views/NotFoundView.vue'
import ThirdPartyResultView from '@/views/ThirdPartyResultView.vue'
import AuthorizeView from '@/views/AuthorizeView.vue'
import DataSourceCallbackView from '@/views/DataSourceCallbackView.vue'
import LoginV3 from '@/components/login/LoginV3.vue'
import ApplicationDetailsViewOverview from '@/views/tabs/ApplicationDetailsViewOverview.vue'

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.dashboard',
    },
  },
  {
    path: '/test',
    name: 'Test',
    component: TestView,
    meta: {
      layout: 'default',
      requireLogin: true,
    },
  },
  {
    path: '/test2',
    name: 'Test2',
    component: Test2View,
    meta: {
      layout: 'default',
      requireLogin: true,
    },
  },
  {
    path: '/users',
    name: 'Users',
    component: PeopleView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.users',
    },
  },
  {
    path: '/documents',
    name: 'Documents',
    component: DocumentsView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.documents',
    },
  },
  {
    path: '/accounting/imports/:id',
    name: 'AccountingImportDetail',
    component: AccountingImportDetailView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'accountingImports',
      customHeader: AccountingImportDetailCustomHeader,
    },
  },
  {
    path: '/applications',
    name: 'Applications',
    component: ApplicationsView,
    props: (route: RouteLocationNormalized) => ({
      scroll: route.query.scroll,
    }),
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.applications',
    },
  },
  {
    path: '/applications/:id/:subAccountId?',
    name: 'Application',
    component: ApplicationDetailsView,
    beforeEnter: async (to: RouteLocationNormalized) => {
      const applicationId =
        typeof to.params.id === 'string' ? to.params.id : undefined
      if (!applicationId) return true

      const requestedSubAccount = normalizeSubAccountParam(
        to.params.subAccountId
      )

      const detailsStore = useApplicationDetailsStore()
      const cachedApplication =
        detailsStore.application?._id === applicationId
          ? detailsStore.application
          : undefined

      let subAccounts = cachedApplication?.sub_accounts

      if (!subAccounts || Object.keys(subAccounts).length === 0) {
        const application =
          await ApplicationStore.getApplicationById(applicationId)
        subAccounts = application?.sub_accounts
      }

      if (!subAccounts) {
        return { name: 'Applications' }
      }

      if (requestedSubAccount && subAccounts[requestedSubAccount]) {
        return true
      }

      const selection = selectPreferredSubAccount(subAccounts)

      if (!selection) {
        return { name: 'Applications' }
      }

      return {
        name:
          typeof to.name === 'string' ? to.name : 'ApplicationDetailsOverview',
        params: {
          ...to.params,
          subAccountId: selection.id,
        },
        query: to.query,
        hash: to.hash,
      }
    },
    meta: {
      layout: 'default',
      requireLogin: true,
      showHeader: false,
      titleKey: 'editApplication',
    },
    children: [
      {
        path: '',
        redirect: { name: 'ApplicationDetailsOverview' },
      },
      {
        path: 'documents',
        component: ApplicationDetailsViewDocuments,
      },
      {
        path: 'notes',
        component: ApplicationDetailsViewNotes,
      },
      {
        path: 'optimizations',
        component: ApplicationDetailsViewOptimizations,
      },
      {
        path: 'licenses',
        component: ApplicationDetailsViewLicenses,
      },
      {
        path: 'accounts',
        name: 'ApplicationDetailsAccounts',
        component: ApplicationDetailsViewAccounts,
      },
      {
        path: 'overview',
        name: 'ApplicationDetailsOverview',
        component: ApplicationDetailsViewOverview,
      },
    ],
  },

  {
    path: '/groups',
    name: 'Groups',
    component: GroupsView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.groups',
    },
  },
  {
    path: '/discovery',
    name: 'Discovery',
    component: DataSourcesView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.discoveryTools',
    },
  },
  {
    path: '/data-sources',
    redirect: { name: 'Discovery' },
  },
  {
    path: '/discovery/callback',
    name: 'Discovery-Callback',
    component: DataSourceCallbackView,
    meta: {
      layout: 'empty',
      requireLogin: false,
    },
  },
  {
    path: '/data-sources/callback',
    redirect: { name: 'Discovery-Callback' },
  },
  {
    path: '/discovery/:id',
    name: 'Discovery-Details',
    component: DataSourceDetailsView,
    meta: {
      layout: 'default',
      requireLogin: true,
      customHeader: DataSourceDetailCustomHeader,
    },
  },
  {
    path: '/data-sources/:id',
    redirect: { name: 'Discovery-Details' },
  },
  {
    path: '/login',
    name: 'Login v3',
    component: LoginV3,
    meta: {
      requireLogin: false,
      layout: 'empty',
      noTransition: true,
    },
    children: [
      {
        path: '',
        name: 'Login',
        component: LoginFormVue,
      },
      {
        path: 'company',
        name: 'Company',
        component: CompanySelect,
      },
      {
        path: 'invite',
        name: 'Invite',
        component: InviteDisplay,
      },
    ],
  },
  {
    path: '/accounts',
    name: 'Accounts',
    component: LicensesView,
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.accounts',
    },
  },
  {
    path: '/licenses',
    redirect: { name: 'Accounts' },
  },
  {
    path: '/automations',
    name: 'Automations',
    component: AutomationsView,
    query: { tab: 'tasks' },
    meta: {
      layout: 'default',
      requireLogin: true,
      titleKey: 'sidebar.automations',
    },
  },
  {
    path: '/actions',
    redirect: { name: 'Automations', query: { tab: 'jobs' } },
  },
  {
    path: '/callback',
    name: 'Erfolgreich Verbunden',
    component: ThirdPartyResultView,
    meta: {
      layout: 'empty',
      requireLogin: false,
      noTransition: true,
    },
  },
  {
    path: '/authorize',
    name: 'Authorize',
    component: AuthorizeView,
    meta: {
      layout: 'empty',
      requireLogin: false,
      noTransition: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: {
      layout: 'default',
      requireLogin: false,
      titleKey: 'views.notFound.text',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const sessionStore = useSessionStore()

  // Hitting any /login route should clear company context
  if (to.path.startsWith('/login')) {
    sessionStore.currentCompany = null
    return
  }

  // Skip protection for public routes
  if (!to.meta.requireLogin) return

  // Block navigation if the user is not authenticated
  sessionStore.requireLogin()

  // Redirect to company selection if no company is selected yet
  const hasCompany = Boolean(sessionStore.currentCompany?.company_id)
  if (!hasCompany) {
    return { name: 'Company' }
  }
})

export default router
